/**
 * Enhanced Organization Sports Management Component
 * Comprehensive interface for managing organization-specific sports
 * Phase 2.5: Frontend Integration with standardized backend API
 */

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import organizationSportsService from "../../services/api/organizationSportsService";
import "../../styles/features/sports.css";
import "../../styles/components/organization-sports.css";

function OrganizationSportsManagement({ token, user }) {
  // State Management
  const [organizationSports, setOrganizationSports] = useState([]);
  const [availableSports, setAvailableSports] = useState([]);
  const [statistics, setStatistics] = useState({});
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("current");

  // Modal States
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [selectedSport, setSelectedSport] = useState(null);

  // Form Data
  const [formData, setFormData] = useState({
    sportId: "",
    facilities: {
      courts: "",
      equipment: "",
      lighting: "",
      surface: "",
    },
    settings: {
      bookingRequired: false,
      maxSessionDuration: 120,
      allowSpectators: true,
    },
    maxParticipants: "",
    contactPerson: "",
  });

  const navigate = useNavigate();

  // Permission Check
  const hasOrgPermissions =
    user &&
    (user.role === "organization_admin" ||
      user.roleName === "organization_admin" ||
      user.role === "super_admin" ||
      user.roleName === "super_admin" ||
      (Array.isArray(user.permissions) &&
        user.permissions.some((p) =>
          [
            "organizations.write",
            "organizations.admin",
            "organizations.*",
          ].includes(typeof p === "string" ? p : p.name)
        )));

  const hasReadPermissions =
    user &&
    (hasOrgPermissions ||
      (Array.isArray(user.permissions) &&
        user.permissions.some((p) =>
          ["organizations.read"].includes(typeof p === "string" ? p : p.name)
        )));

  // Effects
  useEffect(() => {
    if (!hasReadPermissions) {
      navigate("/dashboard");
      return;
    }

    // Data Loading Function
    const initializeData = async () => {
      try {
        setLoading(true);
        setError("");

        await Promise.all([
          fetchOrganizationSports(),
          fetchAvailableSports(),
          fetchStatistics(),
        ]);
      } catch (err) {
        console.error("Failed to initialize data:", err);
        setError(
          "Failed to load organization sports data. Please refresh the page."
        );
      } finally {
        setLoading(false);
      }
    };

    initializeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasReadPermissions, navigate]);

  const fetchOrganizationSports = async () => {
    try {
      const response =
        await organizationSportsService.getOrganizationSports(true);
      if (response.success) {
        setOrganizationSports(response.data || []);
      } else {
        throw new Error(
          response.message || "Failed to fetch organization sports"
        );
      }
    } catch (err) {
      console.error("Failed to fetch organization sports:", err);
      throw err;
    }
  };

  const fetchAvailableSports = async () => {
    try {
      const response = await organizationSportsService.getAllAvailableSports();
      if (response.success) {
        setAvailableSports(response.data || []);
      } else {
        throw new Error(response.message || "Failed to fetch available sports");
      }
    } catch (err) {
      console.error("Failed to fetch available sports:", err);
      throw err;
    }
  };

  const fetchStatistics = async () => {
    if (!hasOrgPermissions) return;

    try {
      const response =
        await organizationSportsService.getOrganizationSportsStats();
      if (response.success) {
        setStatistics(response.data || {});
      }
    } catch (err) {
      console.error("Failed to fetch statistics:", err);
      // Don't throw - statistics are optional
    }
  };

  // CRUD Operations
  const handleAddSport = async () => {
    if (!hasOrgPermissions) {
      setError("You don't have permission to add sports.");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const response = await organizationSportsService.addSportToOrganization(
        formData.sportId,
        {
          facilities: formData.facilities,
          settings: formData.settings,
          maxParticipants: parseInt(formData.maxParticipants) || null,
          contactPerson: formData.contactPerson,
        }
      );

      if (response.success) {
        setMessage("Sport added to organization successfully!");
        setShowAddModal(false);
        resetForm();
        await fetchOrganizationSports();
        await fetchStatistics();
      } else {
        setError(response.message || "Failed to add sport to organization");
      }
    } catch (err) {
      console.error("Failed to add sport:", err);
      setError(
        err.response?.data?.message || "Failed to add sport. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSport = async () => {
    if (!hasOrgPermissions || !selectedSport) {
      setError("You don't have permission to update sports.");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const response = await organizationSportsService.updateOrganizationSport(
        selectedSport.sportId || selectedSport.sport?.id,
        {
          facilities: formData.facilities,
          settings: formData.settings,
          maxParticipants: parseInt(formData.maxParticipants) || null,
          contactPerson: formData.contactPerson,
        }
      );

      if (response.success) {
        setMessage("Sport updated successfully!");
        setShowEditModal(false);
        setSelectedSport(null);
        resetForm();
        await fetchOrganizationSports();
      } else {
        setError(response.message || "Failed to update sport");
      }
    } catch (err) {
      console.error("Failed to update sport:", err);
      setError(
        err.response?.data?.message ||
          "Failed to update sport. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveSport = async (sportId, sportName) => {
    if (!hasOrgPermissions) {
      setError("You don't have permission to remove sports.");
      return;
    }

    if (
      !window.confirm(
        `Are you sure you want to remove "${sportName}" from your organization?`
      )
    ) {
      return;
    }

    try {
      setLoading(true);
      setError("");

      const response =
        await organizationSportsService.removeSportFromOrganization(sportId);

      if (response.success) {
        setMessage("Sport removed from organization successfully!");
        await fetchOrganizationSports();
        await fetchStatistics();
      } else {
        setError(
          response.message || "Failed to remove sport from organization"
        );
      }
    } catch (err) {
      console.error("Failed to remove sport:", err);
      setError(
        err.response?.data?.message ||
          "Failed to remove sport. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  // Form Management
  const resetForm = () => {
    setFormData({
      sportId: "",
      facilities: {
        courts: "",
        equipment: "",
        lighting: "",
        surface: "",
      },
      settings: {
        bookingRequired: false,
        maxSessionDuration: 120,
        allowSpectators: true,
      },
      maxParticipants: "",
      contactPerson: "",
    });
  };

  const openEditModal = (sport) => {
    setSelectedSport(sport);
    setFormData({
      sportId: sport.sportId || sport.sport?.id || "",
      facilities: sport.facilities || {
        courts: "",
        equipment: "",
        lighting: "",
        surface: "",
      },
      settings: sport.settings || {
        bookingRequired: false,
        maxSessionDuration: 120,
        allowSpectators: true,
      },
      maxParticipants: sport.maxParticipants || "",
      contactPerson: sport.contactPerson || "",
    });
    setShowEditModal(true);
  };

  // Helper Functions
  const clearMessage = () => {
    setMessage("");
    setError("");
  };

  const getActiveSports = () =>
    organizationSports.filter((sport) => sport.isActive !== false);
  const getInactiveSports = () =>
    organizationSports.filter((sport) => sport.isActive === false);

  // Filter available sports (exclude already added ones)
  const getUnaddedSports = () => {
    const addedSportIds = organizationSports.map(
      (os) => os.sportId || os.sport?.id
    );
    return availableSports.filter((sport) => !addedSportIds.includes(sport.id));
  };

  if (loading && organizationSports.length === 0) {
    return (
      <div className="organization-sports-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading organization sports...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="organization-sports-container fade-in">
      {/* Header Section */}
      <div className="org-sports-header">
        <div className="header-content">
          <h1>🏢 Organization Sports Management</h1>
          <p>Manage sports available in your organization</p>
        </div>

        <div className="header-actions">
          {hasOrgPermissions && (
            <>
              <button
                className="btn btn-primary"
                onClick={() => setShowAddModal(true)}
                disabled={loading}
              >
                ➕ Add Sport
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => setShowStatsModal(true)}
                disabled={loading}
              >
                📊 Statistics
              </button>
            </>
          )}
          <button
            className="btn btn-outline"
            onClick={() => navigate("/dashboard")}
          >
            ← Back to Dashboard
          </button>
        </div>
      </div>

      {/* Messages */}
      {(message || error) && (
        <div className={`alert ${error ? "alert-error" : "alert-success"}`}>
          <span>{error || message}</span>
          <button onClick={clearMessage} className="alert-close">
            ×
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="tabs-container">
        <div className="tabs">
          <button
            className={`tab ${activeTab === "current" ? "active" : ""}`}
            onClick={() => setActiveTab("current")}
          >
            Current Sports ({getActiveSports().length})
          </button>
          <button
            className={`tab ${activeTab === "inactive" ? "active" : ""}`}
            onClick={() => setActiveTab("inactive")}
          >
            Inactive Sports ({getInactiveSports().length})
          </button>
          <button
            className={`tab ${activeTab === "available" ? "active" : ""}`}
            onClick={() => setActiveTab("available")}
          >
            Available to Add ({getUnaddedSports().length})
          </button>
        </div>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === "current" && (
          <div className="sports-grid">
            {getActiveSports().length === 0 ? (
              <div className="empty-state">
                <h3>No Active Sports</h3>
                <p>Your organization hasn't added any active sports yet.</p>
                {hasOrgPermissions && (
                  <button
                    className="btn btn-primary"
                    onClick={() => setShowAddModal(true)}
                  >
                    Add Your First Sport
                  </button>
                )}
              </div>
            ) : (
              getActiveSports().map((sport) => (
                <div
                  key={sport.id || sport.sportId}
                  className="sport-card active slide-up"
                >
                  <div className="sport-header">
                    <h3>{sport.sport?.name || sport.name}</h3>
                    <span className="sport-status active">Active</span>
                  </div>

                  <div className="sport-details">
                    <p>
                      <strong>Max Participants:</strong>{" "}
                      {sport.maxParticipants || "No limit"}
                    </p>
                    <p>
                      <strong>Contact:</strong>{" "}
                      {sport.contactPerson || "Not specified"}
                    </p>

                    {sport.facilities &&
                      Object.keys(sport.facilities).length > 0 && (
                        <div className="facilities">
                          <strong>Facilities:</strong>
                          <ul>
                            {Object.entries(sport.facilities).map(
                              ([key, value]) =>
                                value && (
                                  <li key={key}>
                                    {key}: {value}
                                  </li>
                                )
                            )}
                          </ul>
                        </div>
                      )}
                  </div>

                  {hasOrgPermissions && (
                    <div className="sport-actions">
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => openEditModal(sport)}
                        disabled={loading}
                      >
                        ✏️ Edit
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() =>
                          handleRemoveSport(
                            sport.sportId || sport.sport?.id,
                            sport.sport?.name || sport.name
                          )
                        }
                        disabled={loading}
                      >
                        🗑️ Remove
                      </button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === "inactive" && (
          <div className="sports-grid">
            {getInactiveSports().length === 0 ? (
              <div className="empty-state">
                <h3>No Inactive Sports</h3>
                <p>All your organization's sports are currently active.</p>
              </div>
            ) : (
              getInactiveSports().map((sport) => (
                <div
                  key={sport.id || sport.sportId}
                  className="sport-card inactive"
                >
                  <div className="sport-header">
                    <h3>{sport.sport?.name || sport.name}</h3>
                    <span className="sport-status inactive">Inactive</span>
                  </div>

                  <div className="sport-details">
                    <p>
                      <strong>Max Participants:</strong>{" "}
                      {sport.maxParticipants || "No limit"}
                    </p>
                    <p>
                      <strong>Contact:</strong>{" "}
                      {sport.contactPerson || "Not specified"}
                    </p>
                  </div>

                  {hasOrgPermissions && (
                    <div className="sport-actions">
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => openEditModal(sport)}
                        disabled={loading}
                      >
                        ✏️ Edit
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() =>
                          handleRemoveSport(
                            sport.sportId || sport.sport?.id,
                            sport.sport?.name || sport.name
                          )
                        }
                        disabled={loading}
                      >
                        🗑️ Remove
                      </button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === "available" && (
          <div className="sports-grid">
            {getUnaddedSports().length === 0 ? (
              <div className="empty-state">
                <h3>All Sports Added</h3>
                <p>
                  Your organization has added all available sports from the
                  platform.
                </p>
              </div>
            ) : (
              getUnaddedSports().map((sport) => (
                <div key={sport.id} className="sport-card available">
                  <div className="sport-header">
                    <h3>{sport.name}</h3>
                    <span className="sport-status available">Available</span>
                  </div>

                  <div className="sport-details">
                    <p>{sport.description || "No description available"}</p>
                    <p>
                      <strong>Category:</strong> {sport.category || "General"}
                    </p>
                    {sport.equipment && (
                      <p>
                        <strong>Equipment:</strong> {sport.equipment}
                      </p>
                    )}
                  </div>

                  {hasOrgPermissions && (
                    <div className="sport-actions">
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => {
                          setFormData((prev) => ({
                            ...prev,
                            sportId: sport.id,
                          }));
                          setShowAddModal(true);
                        }}
                        disabled={loading}
                      >
                        ➕ Add to Organization
                      </button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Add Sport Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Sport to Organization</h2>
              <button
                onClick={() => setShowAddModal(false)}
                className="modal-close"
              >
                ×
              </button>
            </div>

            <form
              id="add-sport-form"
              onSubmit={(e) => {
                e.preventDefault();
                handleAddSport();
              }}
            >
              <div className="modal-body">
                <div className="form-group">
                  <label>Sport *</label>
                  <select
                    value={formData.sportId}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        sportId: e.target.value,
                      }))
                    }
                    required
                  >
                    <option value="">Select a sport...</option>
                    {getUnaddedSports().map((sport) => (
                      <option key={sport.id} value={sport.id}>
                        {sport.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Max Participants</label>
                  <input
                    type="number"
                    value={formData.maxParticipants}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        maxParticipants: e.target.value,
                      }))
                    }
                    placeholder="No limit"
                    min="1"
                  />
                </div>

                <div className="form-group">
                  <label>Contact Person</label>
                  <input
                    type="text"
                    value={formData.contactPerson}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        contactPerson: e.target.value,
                      }))
                    }
                    placeholder="Sports coordinator name"
                  />
                </div>

                <h3>Facilities</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>Courts/Fields</label>
                    <input
                      type="text"
                      value={formData.facilities.courts}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          facilities: {
                            ...prev.facilities,
                            courts: e.target.value,
                          },
                        }))
                      }
                      placeholder="Number of courts/fields"
                    />
                  </div>
                  <div className="form-group">
                    <label>Equipment</label>
                    <input
                      type="text"
                      value={formData.facilities.equipment}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          facilities: {
                            ...prev.facilities,
                            equipment: e.target.value,
                          },
                        }))
                      }
                      placeholder="Available equipment"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Lighting</label>
                    <input
                      type="text"
                      value={formData.facilities.lighting}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          facilities: {
                            ...prev.facilities,
                            lighting: e.target.value,
                          },
                        }))
                      }
                      placeholder="Lighting type"
                    />
                  </div>
                  <div className="form-group">
                    <label>Surface</label>
                    <input
                      type="text"
                      value={formData.facilities.surface}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          facilities: {
                            ...prev.facilities,
                            surface: e.target.value,
                          },
                        }))
                      }
                      placeholder="Surface type"
                    />
                  </div>
                </div>

                <h3>Settings</h3>
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.settings.bookingRequired}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          settings: {
                            ...prev.settings,
                            bookingRequired: e.target.checked,
                          },
                        }))
                      }
                    />
                    Booking Required
                  </label>
                </div>

                <div className="form-group">
                  <label>Max Session Duration (minutes)</label>
                  <input
                    type="number"
                    value={formData.settings.maxSessionDuration}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        settings: {
                          ...prev.settings,
                          maxSessionDuration: parseInt(e.target.value),
                        },
                      }))
                    }
                    min="30"
                    max="480"
                  />
                </div>

                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.settings.allowSpectators}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          settings: {
                            ...prev.settings,
                            allowSpectators: e.target.checked,
                          },
                        }))
                      }
                    />
                    Allow Spectators
                  </label>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="btn btn-outline"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading || !formData.sportId}
                >
                  {loading ? "Adding..." : "Add Sport"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Sport Modal */}
      {showEditModal && selectedSport && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit {selectedSport.sport?.name || selectedSport.name}</h2>
              <button
                onClick={() => setShowEditModal(false)}
                className="modal-close"
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleUpdateSport();
                }}
              >
                <div className="form-group">
                  <label>Max Participants</label>
                  <input
                    type="number"
                    value={formData.maxParticipants}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        maxParticipants: e.target.value,
                      }))
                    }
                    placeholder="No limit"
                    min="1"
                  />
                </div>

                <div className="form-group">
                  <label>Contact Person</label>
                  <input
                    type="text"
                    value={formData.contactPerson}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        contactPerson: e.target.value,
                      }))
                    }
                    placeholder="Sports coordinator name"
                  />
                </div>

                <h3>Facilities</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>Courts/Fields</label>
                    <input
                      type="text"
                      value={formData.facilities.courts}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          facilities: {
                            ...prev.facilities,
                            courts: e.target.value,
                          },
                        }))
                      }
                      placeholder="Number of courts/fields"
                    />
                  </div>
                  <div className="form-group">
                    <label>Equipment</label>
                    <input
                      type="text"
                      value={formData.facilities.equipment}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          facilities: {
                            ...prev.facilities,
                            equipment: e.target.value,
                          },
                        }))
                      }
                      placeholder="Available equipment"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Lighting</label>
                    <input
                      type="text"
                      value={formData.facilities.lighting}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          facilities: {
                            ...prev.facilities,
                            lighting: e.target.value,
                          },
                        }))
                      }
                      placeholder="Lighting type"
                    />
                  </div>
                  <div className="form-group">
                    <label>Surface</label>
                    <input
                      type="text"
                      value={formData.facilities.surface}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          facilities: {
                            ...prev.facilities,
                            surface: e.target.value,
                          },
                        }))
                      }
                      placeholder="Surface type"
                    />
                  </div>
                </div>

                <h3>Settings</h3>
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.settings.bookingRequired}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          settings: {
                            ...prev.settings,
                            bookingRequired: e.target.checked,
                          },
                        }))
                      }
                    />
                    Booking Required
                  </label>
                </div>

                <div className="form-group">
                  <label>Max Session Duration (minutes)</label>
                  <input
                    type="number"
                    value={formData.settings.maxSessionDuration}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        settings: {
                          ...prev.settings,
                          maxSessionDuration: parseInt(e.target.value),
                        },
                      }))
                    }
                    min="30"
                    max="480"
                  />
                </div>

                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.settings.allowSpectators}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          settings: {
                            ...prev.settings,
                            allowSpectators: e.target.checked,
                          },
                        }))
                      }
                    />
                    Allow Spectators
                  </label>
                </div>

                <div className="modal-actions">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="btn btn-outline"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? "Updating..." : "Update Sport"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Statistics Modal */}
      {showStatsModal && (
        <div className="modal-overlay" onClick={() => setShowStatsModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Organization Sports Statistics</h2>
              <button
                onClick={() => setShowStatsModal(false)}
                className="modal-close"
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="stats-grid">
                <div className="stat-card">
                  <h3>{statistics.totalSports || organizationSports.length}</h3>
                  <p>Total Sports</p>
                </div>
                <div className="stat-card">
                  <h3>{statistics.activeSports || getActiveSports().length}</h3>
                  <p>Active Sports</p>
                </div>
                <div className="stat-card">
                  <h3>
                    {statistics.inactiveSports || getInactiveSports().length}
                  </h3>
                  <p>Inactive Sports</p>
                </div>
                <div className="stat-card">
                  <h3>{statistics.totalParticipants || "N/A"}</h3>
                  <p>Total Participants</p>
                </div>
              </div>

              {statistics.mostPopularSport && (
                <div className="popular-sport">
                  <h3>Most Popular Sport</h3>
                  <p>
                    {statistics.mostPopularSport.name} (
                    {statistics.mostPopularSport.participants} participants)
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default OrganizationSportsManagement;
